import { getMisDelivery } from "../../API/getMisDelivery";
import { sendDispositionMailApi } from "../../API/sendDispositionMail";
import { env } from "../../constants";
import { MisDeliveryStatus } from "../../models";
import type { MisdeliveryBolFormValues } from "../../@hooks/useMisdeliveryBolForm";
import {
  EMAIL_TYPES,
  getBolEmailTypeStorageKey,
} from "./BOLEmail.constants";
import { getEmailSubject, getHazmatEmailContent } from "./BOLEmail.utils";
import { notifyMisdeliveryEmailSent } from "./misdeliveryEmailEvents";

type PostBolFlowParams = {
  osdNumber: string;
  hasApprovalPermission: boolean;
  emailType?: string;
  hasChange?: boolean;
  hazmatChangedToYes?: boolean;
};

type PostBolFlowResult = {
  navigateTo: string;
  managerApprovalEmailSent?: boolean;
};

const buildMisdeliveryBolPdfFile = async (
  bol?: Partial<MisdeliveryBolFormValues> | null,
): Promise<File | null> => {
  if (!bol) return null;

  try {
    const [{ pdf }, { default: MisdeliveryBolPdf }] = await Promise.all([
      import("@react-pdf/renderer"),
      import("./pdf/MisdeliveryBolPdf"),
    ]);

    const logoSrc = `${globalThis.location.origin}/Saia_ltl.png`;
    const blob = await pdf(
      <MisdeliveryBolPdf values={bol as MisdeliveryBolFormValues} logoSrc={logoSrc} />,
    ).toBlob();

    const pickup = bol.pickupNumber ? bol.pickupNumber.toString().trim() : "";
    const filename = `Misdelivery_BOL_${pickup}.pdf`;

    return new File([blob], filename, { type: "application/pdf" });
  } catch (error) {
    console.error("Failed to generate manager approval PDF attachment:", error);
    return null;
  }
};

const sendManagerApprovalEmail = async (
  osdNumber: string,
  bol?: Partial<MisdeliveryBolFormValues> | null,
  hasChange?: boolean,
): Promise<void> => {
  const params = {
    to: env.managerEmail ?? "",
    subject: getEmailSubject(bol?.pickupNumber, true),
    html: await getHazmatEmailContent(osdNumber),
    attachmentImageUrls: [],
    emailType: hasChange
      ? EMAIL_TYPES.MSD_BOL_UPDATED_APPROVAL_REQUEST
      : EMAIL_TYPES.HAZMAT_BOL_APPROVAL_REQUEST,
    files: [],
  };

  const headers = {
    "OSD-Module-Name": "misdelivery",
    "OSD-Section-Name": "sendBolMail",
    "OSD-Action-Type": "send",
    "OSD-Exception-Number": osdNumber,
    "OSD-Exception-Type": "MSD",
  };

  const pdfAttachment = await buildMisdeliveryBolPdfFile(bol);
  const files = pdfAttachment ? [pdfAttachment] : [];

  await sendDispositionMailApi(osdNumber, params, files, headers);
};

const shouldEnrichHazmatUnNumber = (
  bol?: Partial<MisdeliveryBolFormValues> | null,
): bol is Partial<MisdeliveryBolFormValues> & { unNumber: string } =>
  bol?.isMisdeliveryHazmat === "Y" && !!bol.unNumber;

const formatUnNaNumber = (match: {
  id?: string | null;
  name?: string | null;
  class?: string | null;
  packingGroup?: string | null;
  label?: string | null;
}): string => {
  const values = [
    match.id,
    match.name,
    match.class,
    match.packingGroup,
    match.label,
  ].map((v) => v ?? "");

  while (values.length && values.at(-1) === "") {
    values.pop();
  }

  return values.join(", ");
};

const enrichHazmatUnNumber = async (
  bol?: Partial<MisdeliveryBolFormValues> | null,
): Promise<void> => {
  if (!shouldEnrichHazmatUnNumber(bol)) {
    return;
  }

  try {
    const { getUnNaNumbersAPI } = await import("../../API/getUnNaNumbers");
    const resp = await getUnNaNumbersAPI({
      search: bol.unNumber,
      page: 1,
      limit: 50,
    });
    const match = resp.data.find((item) => item.sequence === bol.unNumberSequence);

    if (match) {
      bol.unNumber = formatUnNaNumber(match);
    }
  } catch (err) {
    console.error("Failed to fetch UN/NA details", err);
    // Proceed without UN/NA details if fetch fails
  }
};

export const completeMisdeliveryBolPostFlow = async ({
  osdNumber,
  hasApprovalPermission,
  emailType,
  hasChange,
  hazmatChangedToYes,
}: PostBolFlowParams): Promise<PostBolFlowResult> => {
  const latestDetails = await getMisDelivery(osdNumber);
  const latestBol = latestDetails?.bol as Partial<MisdeliveryBolFormValues> | null;

  const isHazmatPendingStatus =
    latestDetails?.investigationStatusNumber === MisDeliveryStatus.hazmatPending;
  const shouldSendManagerApprovalEmail =
    (isHazmatPendingStatus || hazmatChangedToYes) && !hasApprovalPermission;

  if (shouldSendManagerApprovalEmail) {
    await enrichHazmatUnNumber(latestBol);
    await sendManagerApprovalEmail(osdNumber, latestBol, hasChange);
    notifyMisdeliveryEmailSent(osdNumber);

    return {
      navigateTo: `/misdelivery/entry/${encodeURIComponent(osdNumber)}`,
      managerApprovalEmailSent: true,
    };
  }

  const storageKey = getBolEmailTypeStorageKey(osdNumber);
  if (emailType) {
    globalThis.sessionStorage.setItem(storageKey, emailType);
  } else {
    globalThis.sessionStorage.removeItem(storageKey);
  }

  return {
    navigateTo: `/misdelivery/bol/${encodeURIComponent(osdNumber)}/send-email`,
  };
};
